import athenaConfig from '../config';
import AmplitudeUtils from './amplitude-utils';
import {
  ERROR_KEYS,
  ERROR_MESSAGES,
  PASSWORD_EXPIRY_REASON_REMEDIATION,
  PASSWORD_EXPIRY_REASON_TEMP_PASSWORD
} from '../constants';
import { indefiniteWaitPromise } from './common-utils';
import { isRetryableError, makeRequestWithRetry } from './api-retry-utils';
import { getForgotPasswordLink } from './app-utils';

export { isRetryableError };

export function getTransformedError(error?: any) {
  const transformedError = error || {};
  if (error?.errorCode) {
    transformedError.errorSummary = ERROR_MESSAGES[error.errorCode];
  }
  else {
    transformedError.errorSummary = ERROR_MESSAGES[ERROR_KEYS.PASSWORD_RESET_LAMBDA_ERROR];
  }
  // Okta populates error banner with the message passed through error.errorSummary
  // https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L373
  return transformedError;
}

/**
   * Submits a form to the legacy password reset workflow
   * This is used to redirect users to step 2 of the legacy workflow
   * @param username - The username to submit
   * @returns A promise that never resolves (to prevent error flash before redirect)
   */
export function submitLegacyPasswordResetForm(username: string): Promise<void> {
  const legacyUrl = getForgotPasswordLink(settings.clientId || '');
  try {
    // Land the user on step 2 of the legacy workflow
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = legacyUrl!;

    // Create hidden input fields
    const fields = {
      USERNAME: username,
      STEP: 'USERNAME',
      NEXTSTEP: 'QUESTION',
      SUBMITVALUE: 'Next Step',
    };

    Object.entries(fields).forEach(([ name, value ]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();

    // Return a Promise that never resolves to prevent error flash before redirect
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return indefiniteWaitPromise();
  }
  catch (error) {
    AmplitudeUtils.logCustomEvent('PasswordReset::RedirectToLegacyWithUsernameFailed', {
      username,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
      error,
    });
    window.location.href = legacyUrl!;
    // Return a Promise that never resolves to prevent error flash before redirect
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return indefiniteWaitPromise();
  }
}

export interface EligibilityCheckResponse {
  eligible: boolean;
  useLegacyWorkflow?: boolean;
  message?: string;
  errorCode?: string;
  // SSO user fields
  identityproviders?: Record<string, {
    contexts: Record<string, string>;
    nameid: string;
  }>;
}

/**
 * Initialize password reset flow - check user eligibility
 * Lambda will also assign user to appropriate Okta group
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordResetInit({
  username,
  supportEnv,
}: {
  username: string;
  supportEnv?: string;
}): Promise<EligibilityCheckResponse> {
  const response = await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset-init`,
    args: { username, supportEnv },
    retryEventName: 'passwordResetInitRetry',
    metadata: { username },
  });

  return {
    eligible: response.eligible,
    useLegacyWorkflow: response.useLegacyWorkflow,
    message: response.message,
    errorCode: response.errorCode,
    identityproviders: response.identityproviders,
  };
}

export interface PasswordResetResponse {
  success?: {
    name: string;
    href: string;
  };
  sessionToken?: string;
}

/**
 * Complete password reset by calling Lambda endpoint
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function userPasswordReset({
  username,
  newPassword,
  stateHandle,
  symantecSecurityCode,
  supportEnv,
}: {
  username: string;
  newPassword: string;
  stateHandle: string;
  symantecSecurityCode?: string;
  supportEnv?: string;
}): Promise<PasswordResetResponse> {
  const response = await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-password-reset`,
    args: {
      username,
      newPassword,
      stateHandle,
      symantecSecurityCode,
      supportEnv,
    },
    retryEventName: 'passwordResetCompletionRetry',
    metadata: { username },
  });

  // Return the full response from the lambda (which contains the full IDX response from Okta)
  return response;
}

export interface SymantecVIPCheckResponse {
  shouldPrompt: boolean;
  username?: string;
  passwordExpiryReason?: string;
}

export function normalizePasswordExpiryReason(passwordExpiryReason?: unknown): string {
  return passwordExpiryReason === PASSWORD_EXPIRY_REASON_REMEDIATION
    ? PASSWORD_EXPIRY_REASON_REMEDIATION
    : PASSWORD_EXPIRY_REASON_TEMP_PASSWORD;
}

/**
 * Check if user should be prompted for Symantec VIP code
 * Calls Lambda endpoint which validates username against stateHandle
 * and then determines if user is EPCS user
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function shouldPromptForSymantecVIP({
  username,
  stateHandle,
}: {
  username: string;
  stateHandle: string;
}): Promise<SymantecVIPCheckResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/should-prompt-for-symantec-vip`,
      args: {
        username,
        stateHandle,
      },
      retryEventName: 'passwordResetSymantecVIPCheckRetry',
      metadata: { username },
    });

    return {
      shouldPrompt: response.shouldPrompt,
      username: response.username,
      passwordExpiryReason: normalizePasswordExpiryReason(response.passwordExpiryReason),
    };
  }
  catch (error: any) {
    // Log the error but don't throw - default to not prompting
    AmplitudeUtils.logCustomEvent('passwordResetSymantecVIPCheckFailed', {
      stateHandle: stateHandle.substring(0, 10) + '...', // Log partial for security
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    // Default to not prompting for Symantec VIP if check fails
    return {
      shouldPrompt: false,
      passwordExpiryReason: PASSWORD_EXPIRY_REASON_TEMP_PASSWORD,
    };
  }
}

export interface ForgotUsernameResponse {
  success: boolean;
}

export interface AdminCoverageResponse {
  hasAdmin: boolean;
  contextSupportMessages: Array<{
    contextId: string;
    supportMessage: string;
  }>;
}

/**
 * Send forgot username email
 * Retries up to 2 times on 5xx errors (3 total attempts)
 */
export async function forgotUsername({
  email,
  supportEnv,
}: {
  email: string;
  supportEnv?: string;
}): Promise<ForgotUsernameResponse> {
  await makeRequestWithRetry({
    url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/forgot-username`,
    args: { email, supportEnv },
    retryEventName: 'forgotUsernameRetry',
    metadata: { username: email },
  });

  // If request succeeds (200 OK), return success
  return { success: true };
}

/**
 * Fetches admin coverage information for the given anet username.
 * Falls back to default response if call fails.
 *
 * Endpoint: GET /admin-coverage
 * Request: { username: string }
 * Response: AdminCoverageResponse
 */
export async function fetchAdminCoverage(username: string): Promise<AdminCoverageResponse> {
  try {
    const response = await makeRequestWithRetry({
      url: `${athenaConfig.athenaOneResetPasswordLambdaBaseUrl}/user-support-info`,
      args: {
        userLogin: username,
      },
      retryEventName: 'adminCoverageRetry',
      metadata: { username },
    });

    return {
      hasAdmin: response.hasAdminCoverage ?? true,
      contextSupportMessages: response.contextSupportMessages?.map((msg: any) => ({
        contextId: msg.contextId,
        supportMessage: msg.supportMessage,
      })) ?? [],
    };
  }
  catch (error: any) {
    // Log the error but don't throw - default to standard admin message
    AmplitudeUtils.logCustomEvent('adminCoverageFetchFailed', {
      username,
      error: error instanceof Error ? error.message : 'Unknown error',
    });

    // Fallback to default admin coverage
    return {
      hasAdmin: true,
      contextSupportMessages: [],
    };
  }
}
